import { useState, useEffect } from "react";
import { ChevronLeft, X, Crown, AlertTriangle, Calendar, Home, MapPin, Link2, Clock, Pencil, Printer, Star, CheckCircle2, Ban, XCircle, Tag, User, Save, Hourglass } from "lucide-react";
import { paleta } from "./tokens";
import SeletorLocalizacao from "./SeletorLocalizacao";
import { hojeISO } from "./datas";

const TIPOS_IMOVEL = ["Apartamento","Moradia","Terreno","Comercial","Quintinha","Armazém","Escritório","Outro"];
const TIPOLOGIAS = ["T0","T1","T2","T3","T4","T5","T6+"];

const S = {
  overlay: { position:"fixed", inset:0, background:"rgba(11,27,61,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 },
  modal: { background:paleta.navy50, borderRadius:16, maxWidth:700, width:"100%", maxHeight:"92vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 50px rgba(11,27,61,0.3)" },
  label: { fontSize:12.5, fontWeight:500, color:paleta.cinza600, marginBottom:4, display:"block" },
  input: { width:"100%", padding:"9px 12px", border:"1px solid " + paleta.cinza200, borderRadius:8, fontSize:13.5, fontFamily:"inherit", background:"#fff", outline:"none", boxSizing:"border-box", color:paleta.cinza900 },
  g2: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  g3: { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 },
  btn: (cor, bg, borda) => ({ background:bg, color:cor, border: borda ? "1px solid " + paleta.cinza200 : "none", borderRadius:8, padding:"9px 18px", fontSize:13.5, fontWeight:600, cursor:"pointer" }),
  card: { background:"#fff", border:"1px solid " + paleta.cinza200, borderRadius:12, padding:"16px 18px", marginBottom:12 },
  cardTitulo: { fontSize:15.5, fontWeight:600, color:"#fff", background:"linear-gradient(90deg, #0B1B3D 0%, #1E3566 45%, #4A6BA8 100%)", margin:"-16px -18px 14px", padding:"10px 18px", borderRadius:"11px 11px 0 0", display:"flex", alignItems:"center", gap:8 },
};

export function FormVisita({ supabase, lead, profile, onClose, onGuardado, addHistorico, consultores=[], isLider=false, inicial=null, passo="" }) {
  const hoje = hojeISO();
  const [f, setF] = useState({
    id_imovel: "", tipo_imovel: "", tipologia: "", preco: "",
    distrito: "", concelho: "", freguesia: "", morada: "", codigo_postal: "",
    zona: "", link_imovel: "", data: hoje, hora: "10:00", notas: "",
    imobiliaria_externa: "", referencia_externa: "",
    ...(inicial || {}),
  });
  const [saving, setSaving] = useState(false);
  const [conflitoModal, setConflitoModal] = useState(null);
  // Lider/admin pode preencher em nome de outro consultor
  const [agenteEscolhidoId, setAgenteEscolhidoId] = useState(profile?.id || "");
  const consultorEscolhido = consultores.find(c => c.id === agenteEscolhidoId) || profile;
  const consNome = consultorEscolhido?.nome || profile?.nome || "";
  const consId = consultorEscolhido?.id || profile?.id || null;
  const isLiderPreenchendoOutro = isLider && consId !== profile?.id;
  const u = (k,v) => setF(p=>({...p,[k]:v}));
  if (!lead) return null;

  // Identificador único de imóvel para comparação
  const chaveImovel = (v) => {
    if (v.id_imovel) return `INT:${v.id_imovel.trim().toLowerCase()}`;
    if (v.imobiliaria_externa && v.referencia_externa) return `EXT:${v.imobiliaria_externa.trim().toLowerCase()}-${v.referencia_externa.trim().toLowerCase()}`;
    if (v.imovel_externo_morada) return `MOR:${v.imovel_externo_morada.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"")}`;
    return null;
  };
  const horaParaMinutos = (h) => { if (!h) return 0; const [hh,mm] = h.split(":").map(Number); return hh*60 + (mm||0); };
  const detectarConflitos = async () => {
    const chaveNova = chaveImovel({
      id_imovel: f.id_imovel,
      imobiliaria_externa: f.imobiliaria_externa,
      referencia_externa: f.referencia_externa,
      imovel_externo_morada: [f.morada, f.freguesia, f.concelho, f.distrito, f.codigo_postal].filter(Boolean).join(", "),
    });
    const minNova = horaParaMinutos(f.hora);
    // Carregar todas visitas do mesmo dia (excluindo canceladas)
    const { data: visitasDia } = await supabase.from("visitas").select("*").eq("data", f.data).neq("estado", "cancelada");
    const margemMin = 15;
    for (const v of (visitasDia||[])) {
      const minV = horaParaMinutos(v.hora);
      const dentroMargem = Math.abs(minV - minNova) < margemMin;
      const mesmaHoraExacta = v.hora === f.hora;
      const chaveV = chaveImovel(v);
      const mesmoImovel = chaveNova && chaveV && chaveNova === chaveV;
      // Cenário 1: duplicação exacta (cliente + imóvel + data + hora)
      if (String(v.lead_id) === String(lead.id) && mesmoImovel && mesmaHoraExacta) {
        return { tipo: "duplicada", visita: v };
      }
      // Cenário 2: mesmo imóvel à mesma hora exacta, cliente diferente
      if (mesmoImovel && mesmaHoraExacta && String(v.lead_id) !== String(lead.id)) {
        return { tipo: "imovel_ocupado", visita: v };
      }
      // Cenário 3: cliente sobreposto (±15 min, imóvel diferente)
      if (String(v.lead_id) === String(lead.id) && dentroMargem && !mesmoImovel) {
        return { tipo: "cliente_sobreposto", visita: v };
      }
      // Cenário 4: consultor sobreposto (±15 min, locais diferentes)
      if (profile?.id && String(v.consultor_id) === String(profile.id) && dentroMargem && !mesmoImovel && String(v.lead_id) !== String(lead.id)) {
        return { tipo: "consultor_sobreposto", visita: v };
      }
    }
    return null;
  };

  const fazerInsert = async () => {
    setSaving(true);
    const payload = {
      lead_id: lead.id,
      consultor_id: consId || null,
      consultor_nome: consNome || "",
      data: f.data, hora: f.hora,
      id_imovel: f.id_imovel || null,
      tipo_imovel: f.tipo_imovel || null,
      tipologia: f.tipologia || null,
      preco: f.preco ? Number(f.preco) : null,
      zona: f.concelho || f.zona || null,
      imovel_externo_morada: [f.morada, f.freguesia, f.concelho, f.distrito, f.codigo_postal].filter(Boolean).join(", ") || null,
      imovel_externo_agencia: f.imobiliaria_externa || null,
      imovel_externo_referencia: f.referencia_externa || null,
      link_imovel: f.link_imovel || null,
      notas: f.notas || null,
      estado: "agendada",
    };
    const { data, error } = await supabase.from("visitas").insert(payload).select().single();
    if (error) { alert("Erro: " + error.message); setSaving(false); return; }
    const desc = `Visita agendada para ${new Date(f.data).toLocaleDateString("pt-PT")} às ${f.hora}` +
      (f.tipologia || f.zona || f.preco ? ` — ${[f.tipologia, f.zona, f.preco?Number(f.preco).toLocaleString("pt-PT")+" €":""].filter(Boolean).join(" · ")}` : "");
    if (addHistorico) await addHistorico(lead.id, "visita_agendada", desc, null, null, "visitas", data.id, { id_imovel: f.id_imovel, link: f.link_imovel });
    // Avançar estado da lead para "visita" se for menos avançado
    const ordemPipeline = ["novo","em_contacto","qualificada","seguimento","em_avaliacao","em_carteira","visita","proposta","cpcv","financiamento","escritura_agendada","fechado"];
    const atual = lead.estado || "novo";
    const idxA = ordemPipeline.indexOf(atual);
    const idxN = ordemPipeline.indexOf("visita");
    if (atual !== "perdido" && idxA !== -1 && idxN > idxA) {
      await supabase.from("leads").update({ estado: "visita", updated_at: new Date().toISOString() }).eq("id", lead.id);
      const PIPE_LBL = { novo:"Novo", em_contacto:"Em Contacto", qualificada:"Qualificada", seguimento:"Em Seguimento", em_avaliacao:"Em Avaliação", em_carteira:"Em Carteira" };
      await supabase.from("historico").insert({ lead_id: lead.id, user_id: profile?.id||null, user_nome: profile?.nome||"Sistema", tipo:"mudanca_estado", descricao:`🔁 Estado: ${PIPE_LBL[atual]||atual} → Em Visita (auto: visita agendada)` });
    }
    setSaving(false);
    if (onGuardado) onGuardado(data);
    onClose();
  };

  const guardar = async () => {
    if (!f.data || !f.hora) { alert("Indica dia e hora."); return; }
    // Validar campos obrigatórios para imóvel externo
    if (!f.id_imovel && !(f.imobiliaria_externa && f.referencia_externa) && !(f.morada || f.concelho)) {
      alert("Preenche o ID do Imóvel (RE/MAX) OU Imobiliária + Referência (externo) OU pelo menos a morada/zona.");
      return;
    }
    const conflito = await detectarConflitos();
    if (conflito) {
      setConflitoModal(conflito);
      return;
    }
    await fazerInsert();
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e=>e.stopPropagation()}>
        <div style={{ background:"#fff", borderBottom:"1px solid " + paleta.cinza200, padding:"14px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:0 }}>
            <button onClick={onClose} aria-label="Voltar à ficha do contacto" title="Voltar à ficha do contacto" style={{ background:"#fff", border:"1px solid " + paleta.cinza200, borderRadius:8, width:36, height:36, display:"grid", placeItems:"center", cursor:"pointer", color:paleta.navy900, flexShrink:0 }}><ChevronLeft size={18} aria-hidden="true" /></button>
            <div style={{ minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:20, color:paleta.navy900 }}>Agendar visita</div>
              <div style={{ fontSize:12.5, color:paleta.cinza600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.nome_completo || lead.nome || "—"}{passo ? ` · Visita ${passo}` : ""}</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{ background:"none", border:"none", cursor:"pointer", color:paleta.cinza400, display:"grid", placeItems:"center", padding:4 }}><X size={22} aria-hidden="true" /></button>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:"16px 22px 6px" }}>
        {isLider && (
          <div style={{ background: isLiderPreenchendoOutro?paleta.avisoFundo:"#fff", border:"1px solid " + paleta.cinza200, borderRadius:10, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:600, color:paleta.navy900, display:"inline-flex", alignItems:"center", gap:6 }}><Crown size={15} aria-hidden="true" />Preencher em nome de</span>
            <select value={agenteEscolhidoId} onChange={e=>setAgenteEscolhidoId(e.target.value)} style={{ ...S.input, width:"auto", padding:"6px 10px", fontWeight:600 }}>
              <option value={profile?.id}>{profile?.nome} (eu)</option>
              {consultores.filter(c => c.id !== profile?.id).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            {isLiderPreenchendoOutro && <span style={{ fontSize:12.5, color:paleta.aviso, fontWeight:600, display:"inline-flex", alignItems:"center", gap:6 }}><AlertTriangle size={14} aria-hidden="true" />A registar como <strong>{consNome}</strong></span>}
          </div>
        )}

        <div style={S.card}>
          <div style={S.cardTitulo}>Cliente</div>
          <div style={{ fontSize:15, fontWeight:600, color:paleta.cinza900 }}>{lead.nome_completo || lead.nome || "—"}</div>
          <div style={{ fontSize:13.5, color:paleta.cinza600, marginTop:2 }}>{lead.telefone || ""}{lead.telefone && lead.email ? " · " : ""}{lead.email || ""}</div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitulo}><Home size={17} aria-hidden="true" />Imóvel a visitar</div>
          <div style={{ fontSize:12.5, color:paleta.cinza600, marginBottom:12 }}>Preenche o ID do imóvel RE/MAX, ou a imobiliária e a referência se for externo.</div>
          <div style={{ ...S.g3, marginBottom:12 }}>
            <div><label style={S.label}>ID do imóvel RE/MAX</label><input value={f.id_imovel} onChange={e=>u("id_imovel",e.target.value)} placeholder="Ex: 1234-5678" style={S.input} /></div>
            <div><label style={S.label}>Tipo</label><select value={f.tipo_imovel} onChange={e=>u("tipo_imovel",e.target.value)} style={S.input}><option value="">—</option>{TIPOS_IMOVEL.map(o=><option key={o}>{o}</option>)}</select></div>
            <div><label style={S.label}>Tipologia</label><select value={f.tipologia} onChange={e=>u("tipologia",e.target.value)} style={S.input}><option value="">—</option>{TIPOLOGIAS.map(o=><option key={o}>{o}</option>)}</select></div>
          </div>
          {!f.id_imovel && (
            <div style={{ ...S.g2, marginBottom:12, padding:"12px", background:paleta.navy50, borderRadius:10, border:"1px solid " + paleta.cinza200 }}>
              <div><label style={S.label}>Imobiliária externa</label><input value={f.imobiliaria_externa} onChange={e=>u("imobiliaria_externa",e.target.value)} placeholder="Ex: Century 21, ERA..." style={S.input} /></div>
              <div><label style={S.label}>Referência externa</label><input value={f.referencia_externa} onChange={e=>u("referencia_externa",e.target.value)} placeholder="Ex: REF-9876" style={S.input} /></div>
            </div>
          )}
          <div>
            <label style={S.label}>Preço (€)</label>
            <input type="number" value={f.preco} onChange={e=>u("preco",e.target.value)} placeholder="Ex: 180000" style={S.input} />
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitulo}><MapPin size={17} aria-hidden="true" />Localização</div>
          <SeletorLocalizacao value={{ distrito:f.distrito, concelho:f.concelho, freguesia:f.freguesia, morada:f.morada, codigo_postal:f.codigo_postal }} onChange={loc => setF(p=>({...p, ...loc}))} />
        </div>

        <div style={S.card}>
          <div style={S.cardTitulo}><Link2 size={17} aria-hidden="true" />Link do imóvel</div>
          <label style={S.label}>Idealista, Imovirtual, RE/MAX...</label>
          <input value={f.link_imovel} onChange={e=>u("link_imovel",e.target.value)} placeholder="https://..." style={S.input} />
        </div>

        <div style={S.card}>
          <div style={S.cardTitulo}><Clock size={17} aria-hidden="true" />Quando</div>
          <div style={{ ...S.g2, marginBottom:12 }}>
            <div><label style={S.label}>Dia *</label><input type="date" value={f.data} onChange={e=>u("data",e.target.value)} style={S.input} /></div>
            <div><label style={S.label}>Hora *</label><input type="time" value={f.hora} onChange={e=>u("hora",e.target.value)} style={S.input} /></div>
          </div>
          <label style={S.label}>Notas internas</label>
          <textarea value={f.notas} onChange={e=>u("notas",e.target.value)} placeholder="..." rows={3} style={{...S.input, fontFamily:"inherit", resize:"vertical"}} />
        </div>
        </div>

        <div style={{ padding:"12px 22px", background:"#fff", borderTop:"1px solid " + paleta.cinza200, display:"flex", justifyContent:"flex-end", gap:10, flexShrink:0 }}>
          <button onClick={onClose} style={S.btn(paleta.navy900,"#fff",true)}>Cancelar</button>
          <button onClick={guardar} disabled={saving} style={{ ...S.btn("#fff","#1E3566"), border:"1px solid #1E3566", display:"inline-flex", alignItems:"center", gap:8, opacity:saving?0.7:1 }}><Calendar size={16} aria-hidden="true" />{saving ? "A guardar..." : "Agendar visita"}</button>
        </div>
      </div>
      {conflitoModal && (() => {
        const c = conflitoModal;
        const v = c.visita;
        const dt = d => d ? new Date(d).toLocaleDateString("pt-PT") : "—";
        const refExist = v.id_imovel || (v.imovel_externo_agencia && v.imovel_externo_referencia ? `${v.imovel_externo_agencia} · ${v.imovel_externo_referencia}` : v.imovel_externo_morada || "—");
        const titulo = c.tipo === "duplicada" ? "Visita duplicada"
                     : c.tipo === "imovel_ocupado" ? "Imóvel já tem visita à mesma hora"
                     : c.tipo === "cliente_sobreposto" ? "Cliente em sobreposição de horário"
                     : "Consultor em sobreposição de horário";
        const explicacao = c.tipo === "duplicada" ? "Já existe uma visita exactamente igual (mesmo cliente, mesmo imóvel, mesma data e hora)."
                     : c.tipo === "imovel_ocupado" ? "Este imóvel já tem uma visita marcada para a mesma hora com outro cliente."
                     : c.tipo === "cliente_sobreposto" ? `Este cliente já tem uma visita ±15 min num imóvel diferente. Não pode estar em dois sítios ao mesmo tempo.`
                     : `Este consultor já tem uma visita ±15 min noutro local.`;
        const corCab = c.tipo === "duplicada" ? paleta.perigo : paleta.aviso;
        return (
          <div style={{ position:"fixed", inset:0, background:"rgba(11,27,61,0.6)", zIndex:1100, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={()=>setConflitoModal(null)}>
            <div style={{ background:"#fff", borderRadius:14, maxWidth:520, width:"100%", padding:"22px 26px" }} onClick={e=>e.stopPropagation()}>
              <h3 style={{ fontSize:18, fontWeight:700, margin:"0 0 8px 0", color:corCab, display:"flex", alignItems:"center", gap:8 }}><AlertTriangle size={20} aria-hidden="true" />{titulo}</h3>
              <div style={{ fontSize:13, color:"#374151", marginBottom:14, lineHeight:1.5 }}>{explicacao}</div>
              <div style={{ background:paleta.navy50, border:"1px solid " + paleta.cinza200, borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
                <div style={{ fontSize:14, fontWeight:600, color:paleta.navy900, marginBottom:6 }}>Visita já existente</div>
                <div style={{ fontSize:13.5, lineHeight:1.7 }}>
                  <div>Data: {dt(v.data)} às <strong>{v.hora}</strong></div>
                  <div>Imóvel: <strong>{refExist}</strong></div>
                  <div>Consultor: {v.consultor_nome||"—"}</div>
                  <div>Cliente: {String(v.lead_id||"").slice(0,8)}</div>
                </div>
              </div>
              <div style={{ background:"#fff", border:"1px solid " + paleta.cinza200, borderRadius:10, padding:"12px 14px", marginBottom:18 }}>
                <div style={{ fontSize:14, fontWeight:600, color:paleta.navy900, marginBottom:6 }}>Nova visita</div>
                <div style={{ fontSize:13.5, lineHeight:1.7 }}>
                  <div>Data: {dt(f.data)} às <strong>{f.hora}</strong></div>
                  <div>Imóvel: {f.id_imovel ? `ID: ${f.id_imovel}` : (f.imobiliaria_externa && f.referencia_externa ? `${f.imobiliaria_externa} · ${f.referencia_externa}` : [f.morada,f.concelho].filter(Boolean).join(", ") || "—")}</div>
                  <div>Cliente: {lead.nome_completo||lead.nome||"—"}</div>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:8, flexWrap:"wrap" }}>
                <button onClick={()=>setConflitoModal(null)} style={S.btn(paleta.navy900,"#fff",true)}>Cancelar</button>
                <button onClick={()=>setConflitoModal(null)} style={{ ...S.btn("#fff","#1E3566"), border:"1px solid #1E3566" }}>Editar dados</button>
                {c.tipo !== "duplicada" && <button onClick={async ()=>{ setConflitoModal(null); await fazerInsert(); }} style={S.btn(paleta.aviso,"#fff",true)}>Avançar mesmo assim</button>}
                {c.tipo === "duplicada" && <button onClick={async ()=>{ if(window.confirm("Tens a certeza? Esta visita é IDÊNTICA a uma já existente.")){ setConflitoModal(null); await fazerInsert(); } }} style={S.btn(paleta.perigo,"#fff",true)}>Forçar criação</button>}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function imprimirVisita(v, lead) {
  if (!v) return;
  const fmt = n => n ? Number(n).toLocaleString("pt-PT")+" €" : "—";
  const dt = d => d ? new Date(d).toLocaleDateString("pt-PT") : "—";
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Visita — ${lead?.nome||""}</title>
<style>body{font-family:Arial,sans-serif;padding:30px;color:#1a1a2e;max-width:700px;margin:auto}
h1{font-family:Georgia,serif;border-bottom:2px solid #c8102e;padding-bottom:8px}
h2{font-size:13px;text-transform:uppercase;color:#c8102e;border-bottom:1px solid #ece9e3;padding-bottom:3px;margin-top:20px}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 20px;font-size:12px}
.grid div{padding:4px 0;border-bottom:1px dotted #ece9e3}
.grid b{color:#6b7280;font-size:10px;text-transform:uppercase}
@media print{body{padding:15px}}</style></head>
<body><h1>📅 Ficha de Visita</h1>
<h2>Cliente</h2>
<div class="grid">
<div><b>Nome</b><br>${lead?.nome_completo || lead?.nome || "—"}</div>
<div><b>Telefone</b><br>${lead?.telefone||"—"}</div>
<div><b>Email</b><br>${lead?.email||"—"}</div>
<div><b>NIF</b><br>${lead?.nif||"—"}</div>
</div>
<h2>Imóvel a visitar</h2>
<div class="grid">
<div><b>ID Imóvel</b><br>${v.id_imovel||"—"}</div>
<div><b>Tipo</b><br>${v.tipo_imovel||"—"}</div>
<div><b>Tipologia</b><br>${v.tipologia||"—"}</div>
<div><b>Preço</b><br>${fmt(v.preco)}</div>
<div><b>Zona</b><br>${v.zona||"—"}</div>
<div><b>Morada</b><br>${v.imovel_externo_morada||"—"}</div>
<div style="grid-column:1/3"><b>Link</b><br>${v.link_imovel||"—"}</div>
</div>
<h2>Quando</h2>
<div class="grid">
<div><b>Data</b><br>${dt(v.data)}</div>
<div><b>Hora</b><br>${v.hora||"—"}</div>
<div><b>Estado</b><br>${(v.estado||"agendada").toUpperCase()}</div>
<div><b>Consultor</b><br>${v.consultor_nome||"—"}</div>
</div>
${v.estado==="realizada" ? `<h2>Resultado</h2>
<div class="grid">
<div><b>Gostou do imóvel?</b><br>${v.gostou===true?"Sim":v.gostou===false?"Não":"—"}</div>
<div><b>Quer fazer proposta?</b><br>${v.quer_proposta===true?"Sim":v.quer_proposta===false?"Não":"—"}</div>
</div>
${v.feedback?`<div style="margin-top:12px;font-size:12px"><b style="color:#6b7280;font-size:10px;text-transform:uppercase">Comentário</b><br>${v.feedback.replace(/\\n/g,"<br>")}</div>`:""}` : ""}
${v.notas?`<h2>Notas internas</h2><div style="font-size:12px">${v.notas.replace(/\\n/g,"<br>")}</div>`:""}
<div style="margin-top:30px;text-align:center;font-size:10px;color:#9ca3af">Equipa Sónia Cunha · RE/MAX White · Impresso em ${new Date().toLocaleString("pt-PT")}</div>
<script>window.onload=()=>{window.print();}</script></body></html>`;
  const w = window.open("", "_blank", "width=900,height=700");
  if (w) { w.document.write(html); w.document.close(); }
}

export function FichaVisita({ supabase, visita, lead, profile, onClose, onActualizado, addHistorico, onCriarProposta }) {
  const [v, setV] = useState(visita || {});
  const [saving, setSaving] = useState(false);
  const [editando, setEditando] = useState(false);
  useEffect(()=>{ setV(visita||{}); setEditando(false); }, [visita?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!visita) return null;

  const u = (k,val) => setV(p=>({...p,[k]:val}));

  const guardarEdicao = async () => {
    setSaving(true);
    const updates = {
      id_imovel: v.id_imovel || null,
      tipo_imovel: v.tipo_imovel || null,
      tipologia: v.tipologia || null,
      preco: v.preco ? Number(v.preco) : null,
      zona: v.zona || null,
      imovel_externo_morada: v.imovel_externo_morada || null,
      link_imovel: v.link_imovel || null,
      data: v.data || null,
      hora: v.hora || null,
      notas: v.notas || null,
    };
    await supabase.from("visitas").update(updates).eq("id", visita.id);
    if (addHistorico && lead?.id) {
      await addHistorico(lead.id, "visita", `Visita editada — ${[v.id_imovel?`ID ${v.id_imovel}`:"", v.imovel_externo_morada||"", v.tipologia, v.preco?Number(v.preco).toLocaleString("pt-PT")+" €":""].filter(Boolean).join(" · ")}`, null, null, "visitas", visita.id);
    }
    setSaving(false);
    setEditando(false);
    if (onActualizado) onActualizado();
  };

  const marcarEstado = async (novoEstado) => {
    if (v.estado === novoEstado) return;
    setSaving(true);
    await supabase.from("visitas").update({ estado: novoEstado }).eq("id", visita.id);
    if (addHistorico && lead?.id) {
      const tipoMap = { realizada:"visita_realizada", cancelada:"visita_cancelada", nao_realizada:"visita_nao_realizada", agendada:"visita_agendada" };
      const lblMap = { realizada:"realizada", cancelada:"cancelada", nao_realizada:"não realizada", agendada:"reposta para agendada" };
      const desc = `Visita ${lblMap[novoEstado]||novoEstado} (${new Date(visita.data).toLocaleDateString("pt-PT")} ${visita.hora||""})`;
      await addHistorico(lead.id, tipoMap[novoEstado]||"visita", desc, null, null, "visitas", visita.id);
    }
    setV(p=>({...p, estado: novoEstado}));
    setSaving(false);
    if (onActualizado) onActualizado();
  };

  const guardarQualificacao = async () => {
    setSaving(true);
    const updates = {
      gostou: v.gostou ?? null,
      quer_proposta: v.quer_proposta ?? null,
      feedback: v.feedback || null,
    };
    await supabase.from("visitas").update(updates).eq("id", visita.id);
    if (addHistorico && lead?.id) {
      const desc = `Qualificação visita — gostou: ${v.gostou===true?"sim":v.gostou===false?"não":"—"} · proposta: ${v.quer_proposta===true?"sim":v.quer_proposta===false?"não":"—"}`;
      await addHistorico(lead.id, "visita_qualificada", desc, null, null, "visitas", visita.id, { gostou: v.gostou, quer_proposta: v.quer_proposta });
    }
    setSaving(false);
    if (onActualizado) onActualizado();
    if (v.quer_proposta === true && onCriarProposta) {
      onCriarProposta(lead, visita);
      return;
    }
    onClose();
  };

  const CINZA_ACT = "#6B7385";
  const Escolha = ({ label, val, onChange }) => (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, padding:"10px 0", borderBottom:"1px solid " + paleta.cinza200 }}>
      <span style={{ fontSize:14, fontWeight:500, color:paleta.cinza900 }}>{label}</span>
      <div style={{ display:"flex", gap:6 }}>
        {[[true,"Sim"],[false,"Não"]].map(([k,t]) => (
          <button key={t} onClick={()=>onChange(k)} aria-pressed={val===k} style={{ background: val===k?CINZA_ACT:"#fff", color: val===k?"#fff":paleta.navy900, border:"1px solid " + (val===k?CINZA_ACT:paleta.cinza200), borderRadius:8, padding:"7px 18px", fontSize:13.5, fontWeight:600, cursor:"pointer", minWidth:64 }}>{t}</button>
        ))}
      </div>
    </div>
  );

  const realizada = v.estado === "realizada";
  const naoRealizada = v.estado === "nao_realizada";
  const ESTADOS = [
    { id:"agendada", label:"Agendada", Icone:Hourglass },
    { id:"realizada", label:"Realizada", Icone:CheckCircle2 },
    { id:"nao_realizada", label:"Não realizada", Icone:Ban },
    { id:"cancelada", label:"Cancelada", Icone:XCircle },
  ];
  const mostrarQualificar = realizada || naoRealizada || (v.gostou !== null && v.gostou !== undefined);
  const linha = (Icone, children) => (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"6px 0", fontSize:14, color:paleta.cinza900 }}>
      <Icone size={16} aria-hidden="true" style={{ color:paleta.navy700, marginTop:2, flexShrink:0 }} /><div style={{ minWidth:0 }}>{children}</div>
    </div>
  );
  const dataTxt = v.data ? new Date(v.data).toLocaleDateString("pt-PT") : "—";
  const detalhe = [v.tipologia, v.tipo_imovel, v.zona].filter(Boolean).join(" · ") + (v.preco ? `${[v.tipologia, v.tipo_imovel, v.zona].some(Boolean) ? " · " : ""}${Number(v.preco).toLocaleString("pt-PT")} €` : "");
  const btnIcone = { background:"#fff", color:paleta.navy900, border:"1px solid " + paleta.cinza200, borderRadius:8, padding:"7px 12px", fontSize:13, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e=>e.stopPropagation()}>
        <div style={{ background:"#fff", borderBottom:"1px solid " + paleta.cinza200, padding:"14px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexShrink:0, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:0 }}>
            <button onClick={onClose} aria-label="Voltar" title="Voltar" style={{ background:"#fff", border:"1px solid " + paleta.cinza200, borderRadius:8, width:36, height:36, display:"grid", placeItems:"center", cursor:"pointer", color:paleta.navy900, flexShrink:0 }}><ChevronLeft size={18} aria-hidden="true" /></button>
            <div style={{ minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:20, color:paleta.navy900 }}>Visita</div>
              <div style={{ fontSize:12.5, color:paleta.cinza600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead?.nome_completo || lead?.nome || "—"}</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {!editando && <button onClick={() => setEditando(true)} style={btnIcone}><Pencil size={15} aria-hidden="true" />Editar</button>}
            <button onClick={() => imprimirVisita(visita, lead)} style={btnIcone}><Printer size={15} aria-hidden="true" />Imprimir</button>
            <button onClick={onClose} aria-label="Fechar" style={{ background:"none", border:"none", cursor:"pointer", color:paleta.cinza400, display:"grid", placeItems:"center", padding:4 }}><X size={22} aria-hidden="true" /></button>
          </div>
        </div>

        <div style={{ flex:1, overflowY:"auto", padding:"16px 22px 6px" }}>
          <div style={S.card}>
            <div style={S.cardTitulo}><User size={17} aria-hidden="true" />Cliente</div>
            <div style={{ fontSize:15, fontWeight:600, color:paleta.cinza900 }}>{lead?.nome_completo || lead?.nome || "—"}</div>
            <div style={{ fontSize:13.5, color:paleta.cinza600, marginTop:2 }}>{lead?.telefone || ""}{lead?.telefone && lead?.email ? " · " : ""}{lead?.email || ""}</div>
          </div>

          {!editando ? (
            <div style={S.card}>
              <div style={S.cardTitulo}><Home size={17} aria-hidden="true" />Imóvel e data</div>
              {linha(Calendar, <strong>{dataTxt}{v.hora ? ` às ${v.hora}` : ""}</strong>)}
              {detalhe && linha(Home, detalhe)}
              {v.id_imovel && linha(Tag, <span>Ref: <strong>{v.id_imovel}</strong></span>)}
              {v.imovel_externo_morada && linha(MapPin, v.imovel_externo_morada)}
              {v.link_imovel && linha(Link2, <a href={v.link_imovel} target="_blank" rel="noopener noreferrer" style={{ color:paleta.navy700, fontWeight:600 }}>Ver imóvel</a>)}
              {v.notas && <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid " + paleta.cinza200, fontSize:13.5, color:paleta.cinza600, whiteSpace:"pre-wrap" }}><strong style={{ color:paleta.cinza900 }}>Notas internas: </strong>{v.notas}</div>}
            </div>
          ) : (
            <div style={S.card}>
              <div style={S.cardTitulo}><Pencil size={17} aria-hidden="true" />Editar dados da visita</div>
              <div style={{ ...S.g2, marginBottom:12 }}>
                <div><label style={S.label}>ID do imóvel (ref.)</label><input value={v.id_imovel||""} onChange={e=>u("id_imovel",e.target.value)} placeholder="Ex: 1234-5678" style={S.input} /></div>
                <div><label style={S.label}>Preço (€)</label><input type="number" value={v.preco||""} onChange={e=>u("preco",e.target.value)} placeholder="180000" style={S.input} /></div>
                <div><label style={S.label}>Tipo</label><select value={v.tipo_imovel||""} onChange={e=>u("tipo_imovel",e.target.value)} style={S.input}><option value="">—</option>{TIPOS_IMOVEL.map(o=><option key={o}>{o}</option>)}</select></div>
                <div><label style={S.label}>Tipologia</label><select value={v.tipologia||""} onChange={e=>u("tipologia",e.target.value)} style={S.input}><option value="">—</option>{TIPOLOGIAS.map(o=><option key={o}>{o}</option>)}</select></div>
                <div><label style={S.label}>Zona ou concelho</label><input value={v.zona||""} onChange={e=>u("zona",e.target.value)} style={S.input} /></div>
                <div><label style={S.label}>Data</label><input type="date" value={v.data||""} onChange={e=>u("data",e.target.value)} style={S.input} /></div>
                <div><label style={S.label}>Hora</label><input type="time" value={v.hora||""} onChange={e=>u("hora",e.target.value)} style={S.input} /></div>
              </div>
              <div style={{ marginBottom:12 }}><label style={S.label}>Morada</label><input value={v.imovel_externo_morada||""} onChange={e=>u("imovel_externo_morada",e.target.value)} placeholder="Rua, número..." style={S.input} /></div>
              <div style={{ marginBottom:12 }}><label style={S.label}>Link do imóvel</label><input value={v.link_imovel||""} onChange={e=>u("link_imovel",e.target.value)} placeholder="https://..." style={S.input} /></div>
              <div style={{ marginBottom:12 }}><label style={S.label}>Notas internas</label><textarea value={v.notas||""} onChange={e=>u("notas",e.target.value)} rows={2} style={{...S.input, fontFamily:"inherit", resize:"vertical"}} /></div>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
                <button onClick={()=>{ setV(visita); setEditando(false); }} style={S.btn(paleta.navy900,"#fff",true)}>Cancelar</button>
                <button onClick={guardarEdicao} disabled={saving} style={{ ...S.btn("#fff","#1E3566"), border:"1px solid #1E3566", display:"inline-flex", alignItems:"center", gap:8, opacity:saving?0.7:1 }}><Save size={16} aria-hidden="true" />{saving?"A guardar...":"Guardar alterações"}</button>
              </div>
            </div>
          )}

          <div style={S.card}>
            <div style={S.cardTitulo}><Clock size={17} aria-hidden="true" />Estado da visita</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(130px, 1fr))", gap:8 }}>
              {ESTADOS.map(es => {
                const activo = es.id === (v.estado||"agendada");
                const Ic = es.Icone;
                return (
                  <button key={es.id} onClick={()=>marcarEstado(es.id)} disabled={saving||activo} aria-pressed={activo}
                    style={{ background: activo?CINZA_ACT:"#fff", color: activo?"#fff":paleta.navy900, border:"1px solid " + (activo?CINZA_ACT:paleta.cinza200), borderRadius:8, padding:"9px 8px", fontSize:13.5, fontWeight:600, cursor: activo?"default":"pointer", opacity:saving&&!activo?0.6:1, display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                    <Ic size={15} aria-hidden="true" />{es.label}
                  </button>
                );
              })}
            </div>
          </div>

          {mostrarQualificar && (
            <div style={S.card}>
              <div style={S.cardTitulo}><Star size={17} aria-hidden="true" />Qualificar visita</div>
              <Escolha label="Gostou do imóvel?" val={v.gostou} onChange={val=>u("gostou",val)} />
              <Escolha label="Quer fazer proposta?" val={v.quer_proposta} onChange={val=>u("quer_proposta",val)} />
              <div style={{ marginTop:12 }}>
                <label style={S.label}>Comentário</label>
                <textarea value={v.feedback||""} onChange={e=>u("feedback",e.target.value)} rows={3} style={{...S.input, fontFamily:"inherit", resize:"vertical"}} placeholder="O que disse o cliente..." />
              </div>
            </div>
          )}
        </div>

        <div style={{ padding:"12px 22px", background:"#fff", borderTop:"1px solid " + paleta.cinza200, display:"flex", justifyContent:"flex-end", gap:10, flexShrink:0 }}>
          <button onClick={onClose} style={S.btn(paleta.navy900,"#fff",true)}>Fechar</button>
          {mostrarQualificar && <button onClick={guardarQualificacao} disabled={saving} style={{ ...S.btn("#fff","#1E3566"), border:"1px solid #1E3566", display:"inline-flex", alignItems:"center", gap:8, opacity:saving?0.7:1 }}><Save size={16} aria-hidden="true" />{saving ? "A guardar..." : v.quer_proposta === true ? "Guardar e criar proposta" : "Guardar"}</button>}
        </div>
      </div>
    </div>
  );
}
